import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionSignalements.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import signalementService from '../../services/signalementService';

function GestionSignalements() {
  const [signalements, setSignalements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSignalement, setSelectedSignalement] = useState(null);
  const [filters, setFilters] = useState({
    statut: 'all',
    priorite: 'all',
    type_incident: 'all'
  });
  const [stats, setStats] = useState({});

  const statutsOptions = {
    'all': 'Tous les statuts',
    'nouveau': 'Nouveau',
    'en_cours': 'En cours',
    'traite': 'Traité',
    'ferme': 'Fermé'
  };

  const prioritesOptions = {
    'all': 'Toutes les priorités',
    'critique': 'Critique',
    'haute': 'Haute',
    'moyenne': 'Moyenne',
    'basse': 'Basse'
  };

  const typesIncidents = {
    'all': 'Tous les types',
    'harcelement': 'Harcèlement',
    'agression_sexuelle': 'Agression sexuelle',
    'violence_physique': 'Violence physique',
    'discrimination': 'Discrimination',
    'intimidation': 'Intimidation',
    'cyberharclement': 'Cyberharcèlement',
    'autre': 'Autre'
  };

  useEffect(() => {
    loadSignalements();
    loadStatistiques();
  }, [filters]);

  const loadSignalements = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.statut !== 'all') params.statut = filters.statut;
      if (filters.priorite !== 'all') params.priorite = filters.priorite;
      if (filters.type_incident !== 'all') params.type_incident = filters.type_incident;

      const response = await signalementService.admin.getAll(params);
      setSignalements(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistiques = async () => {
    try {
      const response = await signalementService.admin.getStatistiques();
      setStats(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleView = async (id) => {
    try {
      const response = await signalementService.admin.getById(id);
      setSelectedSignalement(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement du signalement');
    }
  };

  const handleUpdateStatut = async (id, statut) => {
    try {
      await signalementService.admin.update(id, { statut });
      alert('Statut mis à jour avec succès');
      loadSignalements();
      if (selectedSignalement && selectedSignalement.id === id) {
        setSelectedSignalement(prev => ({ ...prev, statut }));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleUpdatePriorite = async (id, priorite) => {
    try {
      await signalementService.admin.assignerPriorite(id, priorite);
      alert('Priorité mise à jour avec succès');
      loadSignalements();
      if (selectedSignalement && selectedSignalement.id === id) {
        setSelectedSignalement(prev => ({ ...prev, priorite }));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce signalement ?')) return;
    try {
      await signalementService.admin.delete(id);
      alert('Signalement supprimé avec succès');
      loadSignalements();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const getPrioriteColor = (priorite) => {
    switch (priorite) {
      case 'critique': return '#DC3545';
      case 'haute': return '#FF6B35';
      case 'moyenne': return '#F39C12';
      case 'basse': return '#3498DB';
      default: return '#6C757D';
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'nouveau': return '#DC3545';
      case 'en_cours': return '#F39C12';
      case 'traite': return '#27AE60';
      case 'ferme': return '#6C757D';
      default: return '#6C757D';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Signalements" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          
          {/* Statistiques */}
          <div className="signalements-stats">
            <div className="stat-card" style={{ borderLeftColor: '#DC3545' }}>
              <div className="stat-number">{stats.total || 0}</div>
              <div className="stat-label">Total</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#F39C12' }}>
              <div className="stat-number">{stats.nouveaux || 0}</div>
              <div className="stat-label">Nouveaux</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#FF6B35' }}>
              <div className="stat-number">{stats.en_cours || 0}</div>
              <div className="stat-label">En cours</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#DC3545' }}>
              <div className="stat-number">{stats.critiques || 0}</div>
              <div className="stat-label">Critiques</div>
            </div>
          </div>

          {/* Filtres */}
          <div className="signalements-filters">
            <select 
              value={filters.statut} 
              onChange={(e) => setFilters({...filters, statut: e.target.value})}
            >
              {Object.entries(statutsOptions).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            <select 
              value={filters.priorite} 
              onChange={(e) => setFilters({...filters, priorite: e.target.value})}
            >
              {Object.entries(prioritesOptions).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            <select 
              value={filters.type_incident} 
              onChange={(e) => setFilters({...filters, type_incident: e.target.value})}
            >
              {Object.entries(typesIncidents).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="signalements-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Priorité</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Déclarant</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signalements.map((signalement) => (
                    <tr key={signalement.id}>
                      <td>SIG-{String(signalement.id).padStart(6, '0')}</td>
                      <td>
                        <span className="type-badge">
                          {typesIncidents[signalement.type_incident]}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="priorite-badge" 
                          style={{ backgroundColor: getPrioriteColor(signalement.priorite) }}
                        >
                          {prioritesOptions[signalement.priorite]}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="statut-badge" 
                          style={{ backgroundColor: getStatutColor(signalement.statut) }}
                        >
                          {statutsOptions[signalement.statut]}
                        </span>
                      </td>
                      <td>{formatDate(signalement.created_at)}</td>
                      <td>
                        {signalement.anonyme ? (
                          <span className="anonyme">Anonyme</span>
                        ) : (
                          `${signalement.prenom_declarant} ${signalement.nom_declarant}`
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-view" 
                            onClick={() => handleView(signalement.id)}
                          >
                            Voir
                          </button>
                          <select 
                            value={signalement.statut}
                            onChange={(e) => handleUpdateStatut(signalement.id, e.target.value)}
                            className="statut-select"
                          >
                            <option value="nouveau">Nouveau</option>
                            <option value="en_cours">En cours</option>
                            <option value="traite">Traité</option>
                            <option value="ferme">Fermé</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal de détail */}
          {showModal && selectedSignalement && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content signalement-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Signalement SIG-{String(selectedSignalement.id).padStart(6, '0')}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                
                <div className="signalement-detail">
                  <div className="detail-section">
                    <h3>Informations générales</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Type d'incident:</label>
                        <span>{typesIncidents[selectedSignalement.type_incident]}</span>
                      </div>
                      <div className="detail-item">
                        <label>Priorité:</label>
                        <select 
                          value={selectedSignalement.priorite}
                          onChange={(e) => handleUpdatePriorite(selectedSignalement.id, e.target.value)}
                          style={{ backgroundColor: getPrioriteColor(selectedSignalement.priorite), color: 'white' }}
                        >
                          <option value="critique">Critique</option>
                          <option value="haute">Haute</option>
                          <option value="moyenne">Moyenne</option>
                          <option value="basse">Basse</option>
                        </select>
                      </div>
                      <div className="detail-item">
                        <label>Statut:</label>
                        <select 
                          value={selectedSignalement.statut}
                          onChange={(e) => handleUpdateStatut(selectedSignalement.id, e.target.value)}
                          style={{ backgroundColor: getStatutColor(selectedSignalement.statut), color: 'white' }}
                        >
                          <option value="nouveau">Nouveau</option>
                          <option value="en_cours">En cours</option>
                          <option value="traite">Traité</option>
                          <option value="ferme">Fermé</option>
                        </select>
                      </div>
                      <div className="detail-item">
                        <label>Date de création:</label>
                        <span>{formatDate(selectedSignalement.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Description de l'incident</h3>
                    <p className="incident-description">{selectedSignalement.description}</p>
                  </div>

                  {selectedSignalement.lieu_incident && (
                    <div className="detail-section">
                      <h3>Détails de l'incident</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Lieu:</label>
                          <span>{selectedSignalement.lieu_incident}</span>
                        </div>
                        {selectedSignalement.date_incident && (
                          <div className="detail-item">
                            <label>Date:</label>
                            <span>{new Date(selectedSignalement.date_incident).toLocaleDateString('fr-FR')}</span>
                          </div>
                        )}
                        {selectedSignalement.heure_incident && (
                          <div className="detail-item">
                            <label>Heure:</label>
                            <span>{selectedSignalement.heure_incident}</span>
                          </div>
                        )}
                        {selectedSignalement.personne_impliquee && (
                          <div className="detail-item">
                            <label>Personne impliquée:</label>
                            <span>{selectedSignalement.personne_impliquee}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!selectedSignalement.anonyme && (
                    <div className="detail-section">
                      <h3>Informations du déclarant</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Nom:</label>
                          <span>{selectedSignalement.prenom_declarant} {selectedSignalement.nom_declarant}</span>
                        </div>
                        <div className="detail-item">
                          <label>Relation avec la victime:</label>
                          <span>{selectedSignalement.relation_victime}</span>
                        </div>
                        {selectedSignalement.telephone_contact && (
                          <div className="detail-item">
                            <label>Téléphone:</label>
                            <span>{selectedSignalement.telephone_contact}</span>
                          </div>
                        )}
                        {selectedSignalement.email_contact && (
                          <div className="detail-item">
                            <label>Email:</label>
                            <span>{selectedSignalement.email_contact}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedSignalement.action_souhaitee && (
                    <div className="detail-section">
                      <h3>Action souhaitée</h3>
                      <p>{selectedSignalement.action_souhaitee}</p>
                    </div>
                  )}

                  <div className="modal-actions">
                    <button 
                      className="btn-danger" 
                      onClick={() => handleDelete(selectedSignalement.id)}
                    >
                      Supprimer
                    </button>
                    <button 
                      className="btn-primary" 
                      onClick={() => setShowModal(false)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionSignalements;